/**
 * Pure helpers for the interview overall-progress bar (88 · Task 1.3).
 *
 * The bar binds to the server's aggregate `overallCoverage` fraction ([0,1]).
 * These helpers turn that fraction into the founder-facing percentage + copy.
 * They are trade-secret-safe by construction: they only ever see the collapsed
 * aggregate, never a per-dimension or sub-criterion ratio, and the copy is
 * framed as conversation progress — NEVER a score.
 *
 * Kept standalone (no RN / reanimated imports) so the mapping is unit-tested
 * without the component tree — matches `interview-exit.ts` / `voice-button-state`.
 */

/**
 * Whole-number percentage (0–100) for display + the animated fill width.
 * A missing/loading fraction reads as 0% (the interview simply hasn't started
 * contributing yet); out-of-range inputs are clamped defensively.
 */
export function coveragePercent(coverage: number | null | undefined): number {
  if (coverage == null || !Number.isFinite(coverage)) return 0;
  const clamped = Math.min(1, Math.max(0, coverage));
  return Math.round(clamped * 100);
}

/**
 * Non-numeric, score-free label for the progress bar. Deliberately framed as
 * "conversation progress" so it can never read as a rating of the company.
 */
export function coverageLabel(coverage: number | null | undefined): string {
  return `Conversation progress · ${coveragePercent(coverage)}%`;
}
